import React, { useId, useState } from "react";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

/** Câmp de formular: label vizibil, helper doar la nevoie, eroare inline specifică. */
export function Field({ label, hint, error, required, children, className = "", id: idProp, action }) {
  const auto = useId();
  const id = idProp || auto;
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-err` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <div className="flex items-center justify-between gap-3">
          <label htmlFor={id} className="block text-[13px] font-medium text-cp-text">
            {label}{required && <span className="text-cp-danger ml-0.5" aria-hidden="true">*</span>}
          </label>
          {action}
        </div>
      )}
      {typeof children === "function" ? children({ id, "aria-describedby": describedBy, "aria-invalid": error ? "true" : undefined }) : children}
      {hint && !error && <p id={`${id}-hint`} className="cp-meta">{hint}</p>}
      {error && (
        <p id={`${id}-err`} role="alert" className="text-[12.5px] text-cp-danger flex items-center gap-1.5">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> {error}
        </p>
      )}
    </div>
  );
}

export const Input = React.forwardRef(function Input({ className = "", ...rest }, ref) {
  return <input ref={ref} className={`cp-input ${className}`} {...rest} />;
});

export const Textarea = React.forwardRef(function Textarea({ className = "", ...rest }, ref) {
  return <textarea ref={ref} className={`cp-input ${className}`} {...rest} />;
});

export const Select = React.forwardRef(function Select({ className = "", children, ...rest }, ref) {
  return <select ref={ref} className={`cp-input ${className}`} {...rest}>{children}</select>;
});

export function PasswordInput({ className = "", ...rest }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} className={`cp-input pr-11 ${className}`} {...rest} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Ascunde parola" : "Afișează parola"}
        className="absolute right-1 top-1 h-8 w-8 grid place-items-center rounded text-cp-text-3 hover:text-cp-text hover:bg-cp-surface-active transition-colors">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function Checkbox({ label, className = "", id: idProp, ...rest }) {
  const auto = useId();
  const id = idProp || auto;
  return (
    <label htmlFor={id} className={`flex items-start gap-2.5 cursor-pointer text-[13.5px] text-cp-text-2 leading-snug ${className}`}>
      <input id={id} type="checkbox" className="cp-check mt-0.5 shrink-0" {...rest} />
      <span>{label}</span>
    </label>
  );
}

/** Puterea parolei — feedback inline, fără a bloca. */
export function PasswordStrength({ value = "" }) {
  const checks = [value.length >= 8, /[A-Z]/.test(value), /[0-9]/.test(value), /[^A-Za-z0-9]/.test(value)];
  const score = checks.filter(Boolean).length;
  if (!value) return null;
  const label = ["Prea scurtă", "Slabă", "Acceptabilă", "Bună", "Puternică"][score];
  const tone = score <= 1 ? "bg-cp-danger" : score === 2 ? "bg-cp-warning" : "bg-cp-success";
  return (
    <div className="pt-1" aria-live="polite">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => <span key={i} className={`h-1 flex-1 rounded-full ${i < score ? tone : "bg-cp-neutral-soft"}`} />)}
      </div>
      <p className="cp-meta mt-1.5">{label} · minim 8 caractere; recomandat: literă mare, cifră, simbol</p>
    </div>
  );
}
